import {
  add,
  compact,
  divide,
  every,
  filter,
  find,
  findIndex,
  indexOf,
  isEmpty,
  isString,
  map,
  max,
  multiply,
  reduce,
  some,
  subtract,
  sum,
  toNumber
} from 'lodash';
import { epoch, future, tz, utc } from './datetime';
import { isUUID } from './validate';
import { round } from './transform';
import { predefinedValues } from '../helper/labels';
import { auStatesTypes } from '../model/address/Address.enum';
import { OwnershipType } from '../model/global/OwnershipType.enum';
import { PropertyType } from '../model/global/PropertyType.enum';
import { TransactionType } from '../model/global/TransactionType.enum';
import { PaymentFrequency } from '../model/global/PaymentFrequency.enum';
import { AssetType } from '../model/finance/Asset.enum';
import { IncomeType } from '../model/finance/Income.enum';
import { LiabilityType } from '../model/finance/Liability.enum';
import { ContactEmploymentType } from '../model/contact/ContactEmploymentType.enum';
import { ContactEmploymentBasis } from '../model/contact/ContactEmploymentBasis.enum';
import { ContactResidentialStatusOption } from '../model/contact/ContactResidentialStatusOption.enum';
import Ownership from '../model/finance/Ownership';
import AssetFinanceProposalAmortizationSchedule from '../model/assetFinance/AssetFinanceProposalAmortizationSchedule';
import HomeLoanReviewProduct from '../model/homeLoan/HomeLoanReviewProduct';
import HomeLoanReviewProductSplit from '../model/homeLoan/HomeLoanReviewProductSplit';
import HomeLoanCompareProduct from '../model/homeLoan/HomeLoanCompareProduct';
import HomeLoanCompareProductSplit from '../model/homeLoan/HomeLoanCompareProductSplit';

export const getIdLabelsOfTicketClients = () => [
  '018a40bf-027a-4a08-9910-a0cbb058ddab',
  'ef8154b0-7bda-4d1e-a87e-f286c06f7d94',
  'd583957c-3ee9-4ec2-b041-efa0f8d41875',
  'e542c9e7-ba5c-4ac3-9234-52263b157271'
];

export const isClientClient = (ticketClientTypes: Array<Object>, idContact?: String) => {
  const labels = predefinedValues();
  const client = find(ticketClientTypes || [], (c) => c?.idContact === idContact);
  const label = find(labels || [], (l) => l?.name?.toLowerCase() === 'client');
  return client?.idLabel === label?.id;
};

export const isClientNonBorrowingSpouse = (
  ticketClientTypes: Array<Object>,
  idContact?: String
) => {
  const labels = predefinedValues();
  const nonBorrowingSpouse = find(ticketClientTypes || [], (c) => c?.idContact === idContact);
  const label = find(labels || [], (l) => l?.name?.toLowerCase() === 'non-borrowing spouse');
  return nonBorrowingSpouse?.idLabel === label?.id;
};

export const isClientSecurityGuarantor = (
  ticketClientTypes: Array<Object>,
  idContact?: String
) => {
  const labels = predefinedValues();
  const securityGuarantor = find(ticketClientTypes || [], (c) => c?.idContact === idContact);
  const label = find(labels || [], (l) => l?.name?.toLowerCase() === 'security guarantor');
  return securityGuarantor?.idLabel === label?.id;
};

export const isClientServicingGuarantor = (
  ticketClientTypes: Array<Object>,
  idContact?: String
) => {
  const labels = predefinedValues();
  const servicingGuarantor = find(ticketClientTypes || [], (c) => c?.idContact === idContact);
  const label = find(labels || [], (l) => l?.name?.toLowerCase() === 'servicing guarantor');
  return servicingGuarantor?.idLabel === label?.id;
};

export const isThereAnyContactWhoIsCompanyOrSelfEmployed = (contacts: Array<Object>) =>
  some(contacts || [], (c) => c?.isCompany || c?.isCurrentEmployerSelfEmployed());

export const areAllContactsCompanies = (contacts: Array<Object>) =>
  every(contacts || [], (c) => c?.isCompany);

export const isHouseholdComposedOnlyOfSecurityGuarantorClients = (
  ticketClientTypes: Array<Object>,
  idContacts: Array<Object>
) => {
  const idGuarantors = [];
  const len = idContacts?.length || 0;
  for (let c = 0; c < len; c += 1) {
    const idContact = idContacts[c];
    if (isClientSecurityGuarantor(ticketClientTypes, idContact)) {
      idGuarantors.push(idContact);
    }
  }
  return idGuarantors.length > 0 && idGuarantors.length === idContacts.length;
};

export const isSelfEmployedInACompany = (contact: Object) =>
  some(
    contact?.person?.employments || [],
    (e) =>
      e?.isCurrent &&
      e?.type?.name === ContactEmploymentType.self_employed.name &&
      e?.basis?.name === ContactEmploymentBasis.in_a_company.name
  );

export const getAvailableLendersByLenderCommissions = (
  lenders: Array<Object>,
  organization: Object,
  account: Object,
  workflow: Object,
  isCustomInstance: Boolean,
  isAFCommercial: Boolean
) => {
  let availableLenders = [];
  let filteredLenders = [];
  if (isCustomInstance) {
    const lenderCommissions = filter(
      organization?.enterprise?.lenderCommissions || [],
      (lc) =>
        lc?.upfront !== null &&
        lc?.upfront !== undefined &&
        lc?.upfront >= 0 &&
        lc?.trail !== null &&
        lc?.trail !== undefined &&
        lc?.trail >= 0
    );
    const availableLenderCommissions = map(lenderCommissions || [], 'id');
    if (workflow?.isAssetFinance()) {
      availableLenders = filter(
        lenders || [],
        (l) =>
          indexOf(availableLenderCommissions || [], l?.id) > -1 &&
          (isAFCommercial ? l?.cps?.commercial : l?.cps?.consumer)
      );
    } else if (workflow?.isHomeLoan()) {
      availableLenders = filter(
        lenders || [],
        (l) => indexOf(availableLenderCommissions || [], l?.id) > -1
      );
    } else {
      availableLenders = filter(
        lenders || [],
        (l) => indexOf(availableLenderCommissions || [], l?.id) > -1
      );
    }
  } else {
    availableLenders = lenders || [];
    if (workflow?.isAssetFinance()) {
      availableLenders = filter(lenders || [], (l) =>
        isAFCommercial ? l?.cps?.commercial : l?.cps?.consumer
      );
    }
  }

  const idLenderAccreditations = map(account?.lenderAccreditations || [], (l) => l?.idLender);
  const idLenders = compact(
    map(idLenderAccreditations || [], (p) => {
      if (!isUUID(p)) {
        return p;
      }
      return null;
    })
  );
  if (idLenders) {
    filteredLenders = filter(availableLenders || [], (l) => indexOf(idLenders, l?.id) > -1);
  }

  return filteredLenders;
};

export const getPayment = (
  rate: Number,
  nper: Number,
  pv: Number,
  fv: Number,
  type: Number
) => {
  const futureValue = !fv ? 0 : fv;
  const t = !type ? 0 : type;
  if (rate === 0) {
    return -(pv + futureValue) / nper;
  }
  const pvif = (1 + rate) ** nper;
  let pmt = (rate / (pvif - 1)) * -(pv * pvif - futureValue);
  if (t === 1) {
    pmt /= 1 + rate;
  }
  return pmt < 0 ? -pmt : -pmt;
};

export const setOwnershipOfAssetOrLiability = (contacts: Array<Object>, isFirst: Boolean) => {
  const ownership = [];
  if (contacts?.length) {
    const percent = isFirst ? parseInt(divide(100, contacts.length), 10) : 0;
    for (let c = 0; c < contacts.length; c += 1) {
      ownership.push(Ownership.blank(contacts[c].id, percent));
    }
  }
  return ownership;
};

export const getOwnershipPercentageOfAssetOrLiabilityPerContact = (
  ownership: Array<Object>,
  idContact: String
) => {
  const oP = find(ownership || [], (o) => o?.idContact === idContact);
  return toNumber(oP?.percent || 0);
};

export const hasEducationDebtPerContact = (liabilities: Array<Object>, idContact: String) => {
  const currentLiabilities = [];
  const filteredLiabilities = filter(
    liabilities || [],
    (l) => l?.liabilityType?.id === LiabilityType.education_debt.name && !l?.isRefinanced
  );
  for (let l = 0; l < filteredLiabilities?.length; l += 1) {
    const liability = filteredLiabilities[l];
    const percent = getOwnershipPercentageOfAssetOrLiabilityPerContact(
      liability?.ownership,
      idContact
    );
    if (percent > 0) {
      currentLiabilities.push(liability);
    }
  }
  return currentLiabilities.length > 0;
};

export const getBusinessYear = (
  income: Array<Object>,
  idContact: String,
  businessYear: String
) => {
  let { year } = utc();
  const filteredIncome = filter(
    income || [],
    (i) => i?.incomeType?.name === IncomeType.business_income.name && i?.idContact === idContact
  );
  if (filteredIncome?.length && isString(businessYear)) {
    const years = map(filteredIncome, (i) => toNumber(i?.business[businessYear]?.taxYear));
    year = years?.length ? max(years) : year;
  }
  return year;
};

export const isLiabilityLinkedToAssetMarkedAsToBeSold = (
  assets: Array<Object>,
  idAsset: String
) => {
  const asset = find(assets || [], (a) => a?.id === idAsset && a?.toBeSold);
  return asset !== undefined;
};

export const getInvestmentAssets = (
  assets: Array<Object>,
  homeLoanSecurities: Array<Object>
) => {
  const investmentAssets = filter(
    assets,
    (a) =>
      a?.assetType?.name === AssetType.investment_property_address.name ||
      (a?.assetType?.name === AssetType.owner_occupier_property_address.name &&
        a?.willItBecomeInvestment)
  );
  if (homeLoanSecurities && homeLoanSecurities.length) {
    const idAssets = compact(map(homeLoanSecurities, 'idAsset'));
    for (let i = 0; i < idAssets.length; i += 1) {
      const idAsset = idAssets[i];
      const idx = findIndex(investmentAssets || [], (a) => a?.idAsset === idAsset);
      if (idx > -1) {
        investmentAssets.splice(idx, 1);
      }
    }
  }
  return investmentAssets;
};

export const getInvestmentHomeLoanSecurities = (homeLoanSecurities: Array<Object>) =>
  filter(
    homeLoanSecurities,
    (s) => s?.ownershipType?.name === OwnershipType.investment.name && s?.ownership?.length
  );

export const getOwnerOccupiedAssets = (
  assets: Array<Object>,
  homeLoanSecurities: Array<Object>
) => {
  const ownerOccupiedAssets = filter(
    assets,
    (a) => a?.assetType?.name === AssetType.owner_occupier_property_address.name
  );
  if (homeLoanSecurities && homeLoanSecurities.length) {
    const idAssets = compact(map(homeLoanSecurities, 'idAsset'));
    for (let i = 0; i < idAssets.length; i += 1) {
      const idAsset = idAssets[i];
      const idx = findIndex(ownerOccupiedAssets || [], (a) => a?.idAsset === idAsset);
      if (idx > -1) {
        ownerOccupiedAssets.splice(idx, 1);
      }
    }
  }
  return ownerOccupiedAssets;
};

export const getPurchaseOwnerOccupiedStatusOfAssets = (
  assets: Array<Object>,
  idContact: String
) =>
  some(
    filter(
      assets || [],
      (a) =>
        getOwnershipPercentageOfAssetOrLiabilityPerContact(a?.ownership || [], idContact) > 0
    ),
    (a) =>
      ((a?.assetType?.name === AssetType.owner_occupier_property_address.name &&
        !a?.willItBecomeInvestment) ||
        (a?.assetType?.name === AssetType.investment_property_address.name &&
          a?.willItBecomeOwnerOccupied)) &&
      a?.propertyType?.name !== PropertyType.holiday_home.name &&
      a?.propertyType?.name !== PropertyType.holiday_rental.name &&
      a?.propertyType?.name !== PropertyType.vacant_land.name &&
      !a?.toBeSold
  );

export const getPurchaseOwnerOccupiedStatusOfHomeLoanSecurity = (
  homeLoanSecurities: Array<Object>,
  idContact: String
) =>
  some(
    filter(
      homeLoanSecurities || [],
      (h) =>
        getOwnershipPercentageOfAssetOrLiabilityPerContact(h?.ownership || [], idContact) > 0
    ),
    (s) =>
      (!s?.ownershipType?.name
        ? s?.ownershipType === 1
        : s?.ownershipType?.name === OwnershipType.owner_occupied.name) &&
      (!s?.propertyType?.name
        ? s?.propertyType !== 10
        : s?.propertyType?.name !== PropertyType.holiday_home.name) &&
      (!s?.propertyType?.name
        ? s?.propertyType !== 11
        : s?.propertyType?.name !== PropertyType.holiday_rental.name) &&
      (!s?.propertyType?.name
        ? s?.propertyType !== 13
        : s?.propertyType?.name !== PropertyType.vacant_land.name)
  );

export const getPostSettlementStatus = (
  assets: Array<Object>,
  homeLoanSecurities: Array<Object>,
  contact: Object
) => {
  if (
    getPurchaseOwnerOccupiedStatusOfAssets(assets || [], contact?.id) ||
    getPurchaseOwnerOccupiedStatusOfHomeLoanSecurity(homeLoanSecurities || [], contact?.id)
  ) {
    return false;
  }
  return (
    contact?.person?.contact?.settlementHousing?.name ===
      ContactResidentialStatusOption.boarding.name ||
    contact?.person?.contact?.settlementHousing?.name ===
      ContactResidentialStatusOption.with_parents.name ||
    false
  );
};

export const getPurchaseOwnerOccupiedStatus = (
  contacts: Array<Object>,
  assets: Array<Object>,
  homeLoanSecurities: Array<Object>,
  homeLoanFundingWorksheets: Object,
  isQuote: Boolean
) => {
  const collection = [];
  const len = contacts?.length || 0;
  for (let c = 0; c < len; c += 1) {
    const contact = contacts[c];
    collection.push(
      contact?.person?.contact?.settlementHousing?.name !==
        ContactResidentialStatusOption.none.name &&
        contact?.person?.contact?.settlementHousing?.name !==
          ContactResidentialStatusOption.renting.name &&
        contact?.person?.contact?.settlementHousing?.name !==
          ContactResidentialStatusOption.boarding.name &&
        contact?.person?.contact?.settlementHousing?.name !==
          ContactResidentialStatusOption.with_parents.name
    );
    if (homeLoanSecurities && homeLoanSecurities?.length) {
      collection.push(
        getPurchaseOwnerOccupiedStatusOfHomeLoanSecurity(homeLoanSecurities || [], contact?.id)
      );
    }
    if (assets && assets?.length) {
      collection.push(getPurchaseOwnerOccupiedStatusOfAssets(assets || [], contact?.id));
    }
    if (
      isQuote &&
      !isEmpty(homeLoanFundingWorksheets) &&
      homeLoanFundingWorksheets?.fundsRequired?.length
    ) {
      const fundsRequired = [];
      const fLen = homeLoanFundingWorksheets?.fundsRequired?.length || 0;
      for (let i = 0; i < fLen; i += 1) {
        const fundRequired = homeLoanFundingWorksheets?.fundsRequired[i];
        const security = find(
          homeLoanSecurities || [],
          (s) => s?.id === fundRequired?.idSecurity
        );
        if (
          getOwnershipPercentageOfAssetOrLiabilityPerContact(
            security?.ownership || [],
            contact?.id
          ) > 0
        ) {
          fundsRequired.push(fundRequired);
        }
      }
      collection.push(
        some(fundsRequired, (f) =>
          !f?.ownershipType?.name
            ? f?.ownershipType === 1
            : f?.ownershipType?.name === OwnershipType.owner_occupied.name
        )
      );
    }
  }
  return some(collection, (c) => c === true);
};

export const getOrganizationCountry = (organization: Object, instanceCca2: String) => {
  const name = organization?.location?.name?.common || '';
  const cca2 = organization?.location?.cca2 || instanceCca2;
  return [name, cca2];
};

const getSetState = (state: String) => {
  const states = [
    { id: 'ACT', values: ['ACT', 'Australian Capital Territory'] },
    { id: 'NSW', values: ['NSW', 'New South Wales'] },
    { id: 'NT', values: ['NT', 'Northern Territory'] },
    { id: 'QLD', values: ['QLD', 'Queensland'] },
    { id: 'SA', values: ['SA', 'South Australia'] },
    { id: 'TAS', values: ['TAS', 'Tasmania'] },
    { id: 'VIC', values: ['VIC', 'Victoria'] },
    { id: 'WA', values: ['WA', 'Western Australia'] }
  ];
  const currentState = find(states, (s) =>
    find(s?.values, (v) => v?.toLowerCase() === state?.toLowerCase())
  );
  return currentState?.id || 'ANY';
};

export const getStateAndPostCodePerContact = (
  assets: Array<Object>,
  homeLoanSecurities: Array<Object>,
  contact: Object,
  owner: Object,
  organization: Object,
  instanceCca2: String
) => {
  const [, cca2] = getOrganizationCountry(organization, instanceCca2);
  let state;
  let postCode;
  if (
    !state &&
    !postCode &&
    contact &&
    !contact?.isCompany &&
    contact?.person?.contact?.settlementAddress?.state &&
    contact?.person?.contact?.settlementAddress?.postCode
  ) {
    state = getSetState(contact?.person?.contact?.settlementAddress?.state);
    postCode = contact?.person?.contact?.settlementAddress?.postCode;
  }
  if (
    !state &&
    !postCode &&
    contact &&
    contact?.isCompany &&
    contact?.company?.contact?.address?.state &&
    contact?.company?.contact?.address?.postCode
  ) {
    state = getSetState(contact?.company?.contact?.address?.state);
    postCode = contact?.company?.contact?.address?.postCode;
  }
  if (!state && !postCode && homeLoanSecurities && homeLoanSecurities?.length) {
    let securitiesOO = filter(
      filter(
        homeLoanSecurities,
        (h) =>
          getOwnershipPercentageOfAssetOrLiabilityPerContact(h?.ownership || [], contact?.id) >
          0
      ),
      (s) =>
        s?.ownershipType?.name === OwnershipType.owner_occupied.name &&
        s?.propertyType !== 10 &&
        s?.propertyType !== 11 &&
        s?.propertyType !== 13
    );
    if (cca2 === 'AU') {
      securitiesOO = filter(securitiesOO, (s) => s?.address?.state && s?.address?.postCode);
    }
    if (securitiesOO.length) {
      state = getSetState(securitiesOO[0].address.state);
      postCode = securitiesOO[0].address.postCode;
    }
  }
  if (!state && !postCode && assets && assets?.length) {
    let assetsOO = filter(
      filter(
        assets,
        (obj) =>
          getOwnershipPercentageOfAssetOrLiabilityPerContact(
            obj?.ownership || [],
            contact?.id
          ) > 0
      ),
      (a) =>
        (a?.assetType?.name === AssetType.owner_occupier_property_address.name ||
          (a?.assetType?.name === AssetType.investment_property_address.name &&
            a?.willItBecomeOwnerOccupied)) &&
        !a?.toBeSold
    );
    if (cca2 === 'AU') {
      assetsOO = filter(assetsOO, (s) => s?.address?.state && s?.address?.postCode);
    }
    if (assetsOO.length) {
      state = getSetState(assetsOO[0].address.state);
      postCode = assetsOO[0].address.postCode;
    }
  }
  if (
    !state &&
    !postCode &&
    contact &&
    !contact?.isCompany &&
    contact?.person?.contact?.address?.state &&
    contact?.person?.contact?.address?.postCode
  ) {
    state = getSetState(contact.person.contact.address.state);
    postCode = contact.person.contact.address.postCode;
  }
  if (!state && !postCode && organization?.address?.state && organization?.address?.postCode) {
    state = getSetState(organization.address.state);
    postCode = organization.address.postCode;
  }
  if (
    !state &&
    !postCode &&
    owner &&
    owner?.user?.address?.state &&
    owner?.user?.address?.postCode
  ) {
    state = getSetState(owner.user.address.state);
    postCode = owner.user.address.postCode;
  }
  if (!state && !postCode && cca2 === 'AU') {
    const [, , nsw] = auStatesTypes;
    state = nsw;
    postCode = '2000';
  }
  if (!state && !postCode && cca2 === 'NZ') {
    state = 'ANY';
    postCode = '';
  }
  return [state, postCode];
};

export const getPreferredProduct = (products: Array<Object>) =>
  find(products || [], (p) => p?.isPreferred);

export const fillAssetFinanceCompareProduct = (currentProduct: Object, product: Object) => {
  const productToFill = currentProduct;
  productToFill.productDetails.loanAmount = product?.loanAmount || 0;
  productToFill.productDetails.grossCost = product?.grossCost || 0;
  productToFill.productDetails.amountExGstVat = product?.amountExGstVat || 0;
  productToFill.productDetails.netCost = product?.netCost || 0;
  productToFill.productDetails.gstVatAmount = product?.gstVatAmount || 0;
  productToFill.productDetails.netAmountFinanced = product?.netAmountFinanced || 0;
  productToFill.productDetails.isNetAmountFinancedEdited =
    product?.isNetAmountFinancedEdited || false;
  productToFill.productDetails.netAmountFinancedEdited = product?.netAmountFinancedEdited || 0;
  productToFill.productDetails.term = product?.term || 0;
  productToFill.productDetails.interestRate = product?.interestRate || 0;
  productToFill.productDetails.isInterestRateEdited = product?.isInterestRateEdited || false;
  productToFill.productDetails.brokerCommissionValue = product?.brokerCommissionValue || 0;
  productToFill.productDetails.brokerCommissionPercent = product?.brokerCommissionPercent || 0;
  productToFill.productDetails.rateDiscount = product?.rateDiscount || 0;
  productToFill.productDetails.isRateDiscountEdited = product?.isRateDiscountEdited || false;
  productToFill.productDetails.brokerOriginationFee = product?.brokerOriginationFee || 0;
  productToFill.productDetails.isBrokerOriginationFeeCapitalized =
    product?.isBrokerOriginationFeeCapitalized || false;
  productToFill.productDetails.processingFee = product?.processingFee || 0;
  productToFill.productDetails.brokerFee = product?.brokerFee || 0;
  productToFill.productDetails.upfrontFee = product?.upfrontFee || 0;
  productToFill.productDetails.isUpfrontFeeEdited = product?.isUpfrontFeeEdited || false;
  productToFill.productDetails.isUpfrontFeeCapitalised =
    product?.isUpfrontFeeCapitalised || false;
  productToFill.productDetails.payments = product?.payments || false;
  productToFill.productDetails.paymentFrequency =
    PaymentFrequency.of(product?.paymentFrequency?.toLowerCase()) || PaymentFrequency.monthly;
  productToFill.productDetails.estimatedBorrowerRate = product?.estimatedBorrowerRate || 0;
  productToFill.productDetails.productType = product?.productType || 0;
  productToFill.productDetails.balloonValue = product?.balloonValue || 0;
  productToFill.productDetails.balloonPercent = product?.balloonPercent || 0;
  productToFill.productDetails.lvr = product?.lvr || 0;
  productToFill.productDetails.gstOfEstimatedRepayment = product?.gstOfEstimatedRepayment || 0;
  productToFill.productDetails.estimatedRepaymentWithGst =
    product?.estimatedRepaymentWithGst || 0;
  productToFill.productDetails.loanType = product?.loanType || '';
  productToFill.productDetails.totalBrokerBrokerage = product?.totalBrokerBrokerage || 0;
  productToFill.productDetails.totalAggregatorBrokerage =
    product?.totalAggregatorBrokerage || 0;
  productToFill.productDetails.totalBrokerage = product?.totalBrokerage || 0;
  productToFill.productDetails.aggregatorCommissionValue =
    product?.aggregatorCommissionValue || 0;

  productToFill.repayments.repayment = product?.repayment || 0;
  productToFill.repayments.repaymentWithBuffer = product?.repaymentWithBuffer || 0;
  productToFill.repayments.monthlyFee = product?.monthlyFee || 0;

  productToFill.estimate.netMonthlySurplus = product?.netMonthlySurplus || 0;

  productToFill.costs.principal = product?.totalPrincipal || 0;
  productToFill.costs.interest = product?.totalInterest || 0;
  productToFill.costs.fees = product?.totalFees || 0;
  productToFill.costs.total = product?.totalCosts || 0;

  productToFill.isBestSaving = product?.isBestSaving || false;
  productToFill.comparativeSaving = product?.comparativeSaving || 0;

  return productToFill;
};

export const fillAssetFinanceFinanceProposal = (
  financeProposal: Object,
  product: Object,
  isEstimate: Boolean,
  isQuote: Boolean
) => {
  const financeProposalToFill = financeProposal;
  if (isEstimate) {
    financeProposalToFill.grossCost = product?.grossCost || 0;
    financeProposalToFill.amountExGstVat = product?.amountExGstVat || 0;
    financeProposalToFill.netCost = product?.netCost || 0;
    financeProposalToFill.gstVatAmount = product?.gstVatAmount || 0;
    financeProposalToFill.loanAmount = product?.loanAmount || 0;
    financeProposalToFill.netAmountFinanced = product?.netAmountFinanced || 0;
    financeProposalToFill.isNetAmountFinancedEdited =
      product?.isNetAmountFinancedEdited || false;
    financeProposalToFill.term = product?.term || 0;
    financeProposalToFill.interestRate = product?.interestRate || 0;
    financeProposalToFill.isInterestRateEdited = product?.isInterestRateEdited || false;
    financeProposalToFill.brokerCommissionValue = product?.brokerCommissionValue || 0;
    financeProposalToFill.brokerCommissionPercent = product?.brokerCommissionPercent || 0;
    financeProposalToFill.rateDiscount = product?.rateDiscount || 0;
    financeProposalToFill.isRateDiscountEdited = product?.isRateDiscountEdited || false;
    financeProposalToFill.productType = product?.productType || 0;
    financeProposalToFill.balloonValue = product?.balloonValue || 0;
    financeProposalToFill.balloonPercent = product?.balloonPercent || 0;
    financeProposalToFill.brokerOriginationFee = product?.brokerOriginationFee || 0;
    financeProposalToFill.isBrokerOriginationFeeCapitalized =
      product?.isBrokerOriginationFeeCapitalized || false;
    financeProposalToFill.processingFee = product?.processingFee || 0;
    financeProposalToFill.brokerFee = product?.brokerFee || 0;
    financeProposalToFill.lvr = product?.lvr || 0;
    financeProposalToFill.estimatedBorrowerRate = product?.estimatedBorrowerRate || 0;
    financeProposalToFill.estimatedRepayment = product?.estimatedRepayment || 0;
    // financeProposalToFill.estimatedRepaymentBuffer = product?.estimatedRepaymentBuffer || 0;
    financeProposalToFill.gstOfEstimatedRepayment = product?.gstOfEstimatedRepayment || 0;
    financeProposalToFill.estimatedRepaymentWithGst = product?.estimatedRepaymentWithGst || 0;
    financeProposalToFill.netMonthlySurplus = product?.netMonthlySurplus || 0;
    financeProposalToFill.loanType = product?.loanType || 0;
    financeProposalToFill.totalBrokerBrokerage = product?.totalBrokerBrokerage || 0;
    financeProposalToFill.totalAggregatorBrokerage = product?.totalAggregatorBrokerage || 0;
    financeProposalToFill.totalBrokerage = product?.totalBrokerage || 0;
    financeProposalToFill.aggregatorCommissionValue = product?.aggregatorCommissionValue || 0;
    financeProposalToFill.payments = product?.payments || false;
    financeProposalToFill.paymentFrequency =
      PaymentFrequency.of(product?.paymentFrequency?.toLowerCase()) || PaymentFrequency.monthly;
  } else {
    financeProposalToFill.grossCost = isQuote
      ? product?.grossCost || 0
      : product?.productDetails?.grossCost || 0;
    financeProposalToFill.amountExGstVat = isQuote
      ? product?.amountExGstVat || 0
      : product?.productDetails?.amountExGstVat || 0;
    financeProposalToFill.netCost = isQuote
      ? product?.netCost || 0
      : product?.productDetails?.netCost || 0;
    financeProposalToFill.gstVatAmount = isQuote
      ? product?.gstVatAmount || 0
      : product?.productDetails?.gstVatAmount || 0;
    financeProposalToFill.loanAmount = isQuote
      ? product?.loanAmount || 0
      : product?.productDetails?.loanAmount || 0;
    financeProposalToFill.netAmountFinanced = isQuote
      ? product?.netAmountFinanced || 0
      : product?.productDetails?.netAmountFinanced || 0;
    financeProposalToFill.isNetAmountFinancedEdited = isQuote
      ? product?.isNetAmountFinancedEdited || false
      : product?.productDetails?.isNetAmountFinancedEdited || false;
    financeProposalToFill.term = isQuote
      ? product?.term || 0
      : product?.productDetails?.term || 0;
    financeProposalToFill.interestRate = isQuote
      ? product?.interestRate || 0
      : product?.productDetails?.interestRate || 0;
    financeProposalToFill.isInterestRateEdited = isQuote
      ? product?.isInterestRateEdited || false
      : product?.productDetails?.isInterestRateEdited || false;
    financeProposalToFill.brokerCommissionValue = isQuote
      ? product?.brokerCommissionValue || 0
      : product?.productDetails?.brokerCommissionValue || 0;
    financeProposalToFill.brokerCommissionPercent = isQuote
      ? product?.brokerCommissionPercent || 0
      : product?.productDetails?.brokerCommissionPercent || 0;
    financeProposalToFill.rateDiscount = isQuote
      ? product?.rateDiscount || 0
      : product?.productDetails?.rateDiscount || 0;
    financeProposalToFill.isRateDiscountEdited = isQuote
      ? product?.isRateDiscountEdited || false
      : product?.productDetails?.isRateDiscountEdited || false;
    financeProposalToFill.productType = isQuote
      ? product?.productType || 0
      : product?.productDetails?.productType || 0;
    financeProposalToFill.balloonValue = isQuote
      ? product?.balloonValue || 0
      : product?.productDetails?.balloonValue || 0;
    financeProposalToFill.balloonPercent = isQuote
      ? product?.balloonPercent || 0
      : product?.productDetails?.balloonPercent || 0;
    financeProposalToFill.brokerOriginationFee = isQuote
      ? product?.brokerOriginationFee || 0
      : product?.productDetails?.brokerOriginationFee || 0;
    financeProposalToFill.isBrokerOriginationFeeCapitalized = isQuote
      ? product?.isBrokerOriginationFeeCapitalized || false
      : product?.productDetails?.isBrokerOriginationFeeCapitalized || false;
    financeProposalToFill.processingFee = isQuote
      ? product?.processingFee || 0
      : product?.productDetails?.processingFee || 0;
    financeProposalToFill.brokerFee = isQuote
      ? product?.brokerFee || 0
      : product?.productDetails?.brokerFee || 0;
    financeProposalToFill.lvr = isQuote ? product?.lvr || 0 : product?.productDetails?.lvr || 0;
    financeProposalToFill.estimatedBorrowerRate = isQuote
      ? product?.estimatedBorrowerRate || 0
      : product?.productDetails?.estimatedBorrowerRate || 0;
    financeProposalToFill.estimatedRepayment = isQuote
      ? product?.repayment || 0
      : product?.repayments?.repayment || 0;
    financeProposalToFill.estimatedRepaymentBuffer = isQuote
      ? product?.repaymentBuffer || 0
      : product?.repayments?.repaymentBuffer || 0;
    financeProposalToFill.gstOfEstimatedRepayment = isQuote
      ? product?.gstOfEstimatedRepayment || 0
      : product?.productDetails?.gstOfEstimatedRepayment || 0;
    financeProposalToFill.estimatedRepaymentWithGst = isQuote
      ? product?.estimatedRepaymentWithGst || 0
      : product?.productDetails?.estimatedRepaymentWithGst || 0;
    financeProposalToFill.netMonthlySurplus = isQuote
      ? product?.netMonthlySurplus || 0
      : product?.estimate?.netMonthlySurplus || 0;
    financeProposalToFill.loanType = isQuote
      ? product?.loanType || 0
      : product?.productDetails?.loanType || 0;
    financeProposalToFill.totalBrokerBrokerage = isQuote
      ? product?.totalBrokerBrokerage || 0
      : product?.productDetails?.totalBrokerBrokerage || 0;
    financeProposalToFill.totalAggregatorBrokerage = isQuote
      ? product?.totalAggregatorBrokerage || 0
      : product?.productDetails?.totalAggregatorBrokerage || 0;
    financeProposalToFill.totalBrokerage = isQuote
      ? product?.totalBrokerage || 0
      : product?.productDetails?.totalBrokerage || 0;
    financeProposalToFill.aggregatorCommissionValue = isQuote
      ? product?.aggregatorCommissionValue || 0
      : product?.productDetails?.aggregatorCommissionValue || 0;
    financeProposalToFill.payments = isQuote
      ? product?.payments || false
      : product?.productDetails?.payments || false;
    financeProposalToFill.paymentFrequency = isQuote
      ? PaymentFrequency.of(product?.paymentFrequency?.name?.toLowerCase()) ||
        PaymentFrequency.monthly
      : PaymentFrequency.of(product?.productDetails?.paymentFrequency?.name?.toLowerCase()) ||
        PaymentFrequency.monthly;
  }

  return financeProposalToFill;
};

const getAssetFinanceFinanceProposalAmortizationSchedulePaymentDate = (
  paymentFrequency: Object,
  idx: Number,
  paymentDate: Number,
  timeZone,
  locale,
  payments: Boolean
) => {
  let timeUnit = 'months';
  let timeDuration = 1;
  switch (paymentFrequency?.name) {
    case PaymentFrequency.annually.name:
      timeUnit = 'years';
      break;
    case PaymentFrequency.quarterly.name:
      timeUnit = 'quarters';
      break;
    case PaymentFrequency.fortnightly.name:
      timeUnit = 'weeks';
      timeDuration = 2;
      break;
    default:
      break;
  }
  const date = tz((paymentDate ? utc(paymentDate) : utc()).startOf('day'), timeZone);
  const paymentDateObject = payments
    ? date
    : future(tz(utc(paymentDate), timeZone), timeUnit, timeDuration);
  return epoch(paymentDateObject);
};

const generateAssetFinanceFinanceProposalAmortizationSchedules = (
  amortizationSchedules: Array<Object>,
  netAmountFinanced: Number,
  estimatedRepayment: Number,
  rate: Number,
  nper: Number,
  balloonValue: Number,
  paymentFrequency: Object,
  payments: Boolean,
  ticketDate: Number,
  timeZone: String,
  locale: String
) => {
  for (let i = 0; i < nper; i += 1) {
    const amortizationSchedule = amortizationSchedules[i];
    amortizationSchedule.paymentDate =
      getAssetFinanceFinanceProposalAmortizationSchedulePaymentDate(
        paymentFrequency,
        i,
        i > 0 ? amortizationSchedules[i - 1]?.paymentDate || 0 : ticketDate || 0,
        timeZone,
        locale,
        payments
      );
    if (!amortizationSchedule.isRepaymentEdited) {
      amortizationSchedule.repayment = estimatedRepayment;
    }
    amortizationSchedule.beginningBalance =
      i === 0
        ? add(multiply(amortizationSchedule.repayment, nper), balloonValue)
        : add(
            amortizationSchedules[i - 1]?.endingBalance || 0,
            amortizationSchedules[0]?.interestDeferred || 0
          );
    if (payments) {
      amortizationSchedule.interestPaid =
        i === 0
          ? 0
          : multiply(
              subtract(
                amortizationSchedule?.beginningBalance || 0,
                amortizationSchedules[0]?.interestDeferred || 0
              ),
              rate
            );
    } else {
      amortizationSchedule.interestPaid =
        i === 0
          ? multiply(netAmountFinanced, rate)
          : multiply(
              subtract(
                amortizationSchedule?.beginningBalance || 0,
                amortizationSchedules[0]?.interestDeferred || 0
              ),
              rate
            );
    }
    amortizationSchedule.interestDeferred =
      i === 0
        ? add(
            subtract(multiply(amortizationSchedule?.repayment || 0, nper), netAmountFinanced),
            balloonValue
          )
        : subtract(
            amortizationSchedules[i - 1]?.interestDeferred || 0,
            payments
              ? amortizationSchedule?.interestPaid || 0
              : amortizationSchedules[i - 1]?.interestPaid || 0
          );
    amortizationSchedule.principalReduction = subtract(
      amortizationSchedule.repayment,
      amortizationSchedule.interestPaid
    );
    amortizationSchedule.endingBalance = subtract(
      subtract(
        i === 0
          ? add(multiply(amortizationSchedule?.repayment || 0, nper), balloonValue)
          : amortizationSchedule?.beginningBalance || 0,
        amortizationSchedule?.principalReduction || 0
      ),
      amortizationSchedules[0]?.interestDeferred || 0
    );
  }
  return amortizationSchedules;
};

const prepareAssetFinanceFinanceProposalAmortizationSchedules = (
  financeProposal: Object,
  nper,
  rate,
  ticketDate: Number,
  timeZone: String,
  locale: String
) => {
  let amortizationSchedulesOutput = [];
  const changedRepayments = filter(
    financeProposal?.amortizationSchedules || [],
    (r) => r.isRepaymentEdited
  );
  const unchangedRepayments = filter(
    financeProposal?.amortizationSchedules || [],
    (r) => !r.isRepaymentEdited
  );
  const lenNCR = changedRepayments.length;
  const lenNuCR = unchangedRepayments.length;
  if (lenNCR) {
    const sumEditedRepayments = reduce(
      changedRepayments,
      (memo, num) => memo + parseFloat(num?.repayment || 0),
      0
    );
    const xFactor =
      sumEditedRepayments || lenNCR
        ? divide(
            sumEditedRepayments,
            subtract(lenNCR, multiply(lenNCR, unchangedRepayments[0].repayment))
          )
        : 0;
    const nafX = subtract(financeProposal?.netAmountFinanced || 0, xFactor);
    const repaymentOfFirstIteration = getPayment(
      rate,
      nper,
      nafX,
      financeProposal?.balloonValue || 0,
      financeProposal?.payments ? 1 : 0
    );
    const firstAmortizationSchedules = JSON.parse(
      JSON.stringify(financeProposal?.amortizationSchedules || [])
    );
    const amortizationSchedulesOfFirstIteration =
      generateAssetFinanceFinanceProposalAmortizationSchedules(
        firstAmortizationSchedules,
        financeProposal?.netAmountFinanced || 0,
        repaymentOfFirstIteration,
        rate,
        nper,
        financeProposal?.balloonValue || 0,
        financeProposal?.paymentFrequency || PaymentFrequency.monthly,
        financeProposal?.payments || false,
        ticketDate,
        timeZone,
        locale
      );
    const lastEndingBalanceOfAmortizationSchedulesOfFirstIteration =
      amortizationSchedulesOfFirstIteration[amortizationSchedulesOfFirstIteration.length - 1]
        .endingBalance;
    const yFactor = financeProposal?.payments
      ? divide(lastEndingBalanceOfAmortizationSchedulesOfFirstIteration, subtract(lenNuCR, 1))
      : divide(lastEndingBalanceOfAmortizationSchedulesOfFirstIteration, lenNuCR);
    const repaymentOfSecondIteration = add(repaymentOfFirstIteration, yFactor);
    const secondAmortizationSchedules = JSON.parse(
      JSON.stringify(financeProposal?.amortizationSchedules || [])
    );
    const amortizationSchedulesOfSecondIteration =
      generateAssetFinanceFinanceProposalAmortizationSchedules(
        secondAmortizationSchedules,
        financeProposal?.netAmountFinanced || 0,
        repaymentOfSecondIteration,
        rate,
        nper,
        financeProposal?.balloonValue || 0,
        financeProposal?.paymentFrequency || PaymentFrequency.monthly,
        financeProposal?.payments || false,
        ticketDate,
        timeZone,
        locale
      );
    const lastEndingBalanceOfAmortizationSchedulesOfSecondIteration =
      amortizationSchedulesOfSecondIteration[amortizationSchedulesOfSecondIteration.length - 1]
        .endingBalance;
    const newRepayment = add(
      repaymentOfFirstIteration,
      divide(
        multiply(
          subtract(
            financeProposal?.balloonValue || 0,
            lastEndingBalanceOfAmortizationSchedulesOfFirstIteration
          ),
          subtract(repaymentOfSecondIteration, repaymentOfFirstIteration)
        ),
        subtract(
          lastEndingBalanceOfAmortizationSchedulesOfSecondIteration,
          lastEndingBalanceOfAmortizationSchedulesOfFirstIteration
        )
      )
    );
    amortizationSchedulesOutput = generateAssetFinanceFinanceProposalAmortizationSchedules(
      financeProposal?.amortizationSchedules || [],
      financeProposal?.netAmountFinanced || 0,
      newRepayment,
      rate,
      nper,
      financeProposal?.balloonValue || 0,
      financeProposal?.paymentFrequency || PaymentFrequency.monthly,
      financeProposal?.payments || false,
      ticketDate,
      timeZone,
      locale
    );
  } else {
    amortizationSchedulesOutput = generateAssetFinanceFinanceProposalAmortizationSchedules(
      financeProposal?.amortizationSchedules || [],
      financeProposal?.netAmountFinanced || 0,
      financeProposal?.estimatedRepayment || 0,
      rate,
      nper,
      financeProposal?.balloonValue || 0,
      financeProposal?.paymentFrequency || PaymentFrequency.monthly,
      financeProposal?.payments || false,
      ticketDate,
      timeZone,
      locale
    );
  }
  return amortizationSchedulesOutput;
};

export const setAssetFinanceFinanceProposalAmortizationSchedules = (
  financeProposal: Object,
  ticketDate: Number,
  timeZone: String,
  locale: String
) => {
  const fP = financeProposal;
  if (
    fP?.netAmountFinanced &&
    fP?.interestRate &&
    fP?.estimatedBorrowerRate &&
    fP?.term &&
    fP?.paymentFrequency?.name !== PaymentFrequency.none.name
  ) {
    const rate =
      divide(
        divide(fP.estimatedBorrowerRate, 100),
        PaymentFrequency.getFrequencyValue(
          fP?.paymentFrequency?.name || PaymentFrequency.monthly.name
        )
      ) || 0;
    const nper =
      divide(
        multiply(
          fP.term,
          PaymentFrequency.getFrequencyValue(
            fP?.paymentFrequency?.name || PaymentFrequency.monthly.name
          )
        ),
        12
      ) || 0;

    if (!fP?.amortizationSchedules?.length) {
      const amortizationSchedules = [];
      for (let i = 0; i < nper || 0; i += 1) {
        const amortizationSchedule = AssetFinanceProposalAmortizationSchedule.blank();
        amortizationSchedule.paymentDate =
          getAssetFinanceFinanceProposalAmortizationSchedulePaymentDate(
            fP?.paymentFrequency || PaymentFrequency.monthly,
            i,
            i > 0 ? amortizationSchedules[i - 1]?.paymentDate || 0 : ticketDate || 0,
            timeZone,
            locale,
            fP?.payments || false
          );
        amortizationSchedules.push(amortizationSchedule);
      }
      fP.amortizationSchedules = amortizationSchedules;
    }
    const amortizationSchedules = prepareAssetFinanceFinanceProposalAmortizationSchedules(
      fP || {},
      nper,
      rate,
      ticketDate || epoch(utc().startOf('day')),
      timeZone || 'UTC',
      locale || 'en-AU'
    );
    fP.amortizationSchedules = amortizationSchedules;
  } else {
    fP.amortizationSchedules = [];
  }
  return fP;
};

export const getHomeLoanSecurityValue = (
  fundRequired: Object,
  idPropertyStatus: Number,
  idConstructionType: Number,
  type: String,
  state: String
) => {
  let securityValue = 0;
  const builderContractPrice = toNumber(fundRequired?.builderContractPrice || 0);
  const landValue = toNumber(fundRequired?.landValue || 0);
  switch (idPropertyStatus) {
    case 1:
    case 4:
      securityValue = builderContractPrice;
      break;
    case 2:
      securityValue = landValue;
      break;
    case 3:
      if (idConstructionType === 1 || idConstructionType === 2 || idConstructionType === 4) {
        if (state !== 'TAS') {
          securityValue = type === 'fhog' ? add(builderContractPrice, landValue) : landValue;
        } else {
          securityValue = type === 'fees' ? landValue : add(builderContractPrice, landValue);
        }
      }
      if (idConstructionType === 3) {
        securityValue = add(builderContractPrice, landValue);
      }
      break;
    case 5:
      if (idConstructionType === 2 || idConstructionType === 4) {
        securityValue = add(builderContractPrice, landValue);
      }
      if (idConstructionType === 3) {
        securityValue =
          type === 'stamp_duty' ? add(builderContractPrice, landValue) : builderContractPrice;
      }
      break;
    default:
      break;
  }
  return securityValue;
};

export const getHomeLoanPropertyStatus = (
  idPropertyStatus: Number,
  idConstructionType: Number,
  state: String,
  type: String,
  isValue: Boolean
) => {
  if (!isValue) {
    let status = 'ESTABLISHED_PROPERTY';
    switch (idPropertyStatus) {
      case 2:
        status = 'VACANT_LAND';
        break;
      case 3:
        if (idConstructionType === 1 || idConstructionType === 2 || idConstructionType === 4) {
          if (state !== 'TAS') {
            status = type === 'fhog' ? 'NEW_BUILDING' : 'VACANT_LAND';
          } else {
            status = type === 'fees' ? 'VACANT_LAND' : 'NEW_BUILDING';
          }
        }
        if (idConstructionType === 3) {
          status = type === 'stamp_duty' ? 'CONSTRUCTION' : 'VACANT_LAND';
        }
        break;
      case 4:
        status = 'NEW_BUILDING';
        break;
      case 5:
        if (idConstructionType === 2 || idConstructionType === 4) {
          status = type === 'fhog' ? 'NEW_BUILDING' : 'CONSTRUCTION';
        }
        if (idConstructionType === 3) {
          status = type === 'fhog' ? 'ESTABLISHED_PROPERTY' : 'CONSTRUCTION';
        }
        break;
      default:
        break;
    }
    return status;
  }
  // TODO: This is currently here until the ENUM of property status is created
  const availablePropertyStatus = [
    {
      id: 1,
      value: 'Established property'
    },
    {
      id: 2,
      value: 'Vacant land'
    },
    {
      id: 3,
      value: 'Land and construction'
    },
    {
      id: 4,
      value: 'New building'
    },
    {
      id: 5,
      value: 'Construction'
    }
  ];
  // ------
  const propertyStatus = find(availablePropertyStatus || [], (p) => p?.id === idPropertyStatus);
  return propertyStatus?.value || '';
};

export const getHomeLoanOwnershipType = (ownershipType) => {
  const o = OwnershipType.of(ownershipType?.name || ownershipType?.toString());
  return o?.name?.toUpperCase();
};

export const getHomeLoanTransactionType = (transactionType) => {
  const t = TransactionType.of(transactionType?.name || transactionType?.toString());
  return t?.name?.toUpperCase();
};

export const sumOfHomeLoanFundingWorksheetOtherFundAvailableContributions = (
  fundAvailable: Object
) => {
  let total = 0;
  if (!isEmpty(fundAvailable)) {
    total = sum([
      toNumber(fundAvailable?.firstHomeOwnersGrant || 0),
      toNumber(fundAvailable?.saleProceedFunds || 0),
      toNumber(fundAvailable?.savings || 0),
      toNumber(fundAvailable?.depositPaid || 0),
      toNumber(fundAvailable?.equityFromProperty || 0),
      toNumber(fundAvailable?.gift || 0),
      toNumber(fundAvailable?.otherFundsAvailable || 0)
    ]);
  }
  return total;
};

export const updateHomeLoanFundingWorksheetTotalProposedLoanAmountAndLvr = (
  fundingWorksheets: Object,
  idFundRequired: Object,
  idFundAvailable: Object,
  field: String,
  isCrossCollateralized: Boolean
) => {
  const fundRequired = find(
    fundingWorksheets?.fundsRequired || [],
    (f) => f?.id === idFundRequired
  );
  const fundAvailable = find(
    fundingWorksheets?.fundsAvailable || [],
    (f) => f?.id === idFundAvailable
  );
  let baseLvr = toNumber(fundAvailable?.baseLvr || 0);
  let proposedLoanAmount = toNumber(fundAvailable?.proposedLoanAmount || 0);
  let totalProposedLoanAmount = toNumber(fundAvailable?.totalProposedLoanAmount || 0);
  let totalLvr = toNumber(fundAvailable?.totalLvr || 0);

  if (fundRequired && fundAvailable) {
    let purposeFunds = 0;
    let loanAmount = toNumber(fundAvailable?.proposedLoanAmount || 0);
    let existingLoanBalance = 0;
    const otherLoansAgainstSecurity = toNumber(fundRequired?.otherLoansAgainstSecurity || 0);
    let hasTopUpOrExisting = false;

    if (isCrossCollateralized) {
      purposeFunds = reduce(
        fundingWorksheets?.fundsRequired || [],
        (memo, n) => add(memo, toNumber(n?.purposeFunds || 0)),
        0
      );
      const refinanceBalance = reduce(
        filter(
          fundingWorksheets?.fundsRequired || [],
          (f) => f?.transactionType?.name === TransactionType.refinance.name
        ),
        (memo, n) => add(memo, toNumber(n?.existingLoanBalance || 0)),
        0
      );
      const difference = add(
        subtract(
          subtract(refinanceBalance, loanAmount),
          sumOfHomeLoanFundingWorksheetOtherFundAvailableContributions(
            fundingWorksheets?.fundsAvailable[0]
          )
        ),
        otherLoansAgainstSecurity
      );
      if (difference > 0) {
        loanAmount += difference;
      }
      loanAmount += reduce(
        filter(
          fundingWorksheets?.fundsRequired || [],
          (f) =>
            f?.transactionType?.name === TransactionType.top_up_or_increase.name ||
            f?.transactionType === 3 ||
            f?.transactionType?.name === TransactionType.equity_release.name ||
            f?.transactionType === 4 ||
            ((f?.transactionType?.name === TransactionType.purchase.name ||
              f?.transactionType === 1) &&
              f?.propertyStatus === 5)
        ),
        (memo, n) =>
          add(
            memo,
            add(
              toNumber(n?.existingLoanBalance || 0),
              toNumber(n?.otherLoansAgainstSecurity || 0)
            )
          ),
        0
      );
      hasTopUpOrExisting = some(
        fundingWorksheets?.fundsRequired || [],
        (f) =>
          f?.transactionType?.name === TransactionType.top_up_or_increase.name ||
          f?.transactionType === 3 ||
          f?.transactionType?.name === TransactionType.equity_release.name ||
          f?.transactionType === 4
      );
    } else {
      purposeFunds = toNumber(fundRequired?.purposeFunds || 0);
      existingLoanBalance = toNumber(fundRequired?.existingLoanBalance || 0);
      if (
        fundRequired?.transactionType?.name === TransactionType.top_up_or_increase.name ||
        fundRequired?.transactionType === 3 ||
        fundRequired?.transactionType?.name === TransactionType.equity_release.name ||
        fundRequired?.transactionType === 4 ||
        ((fundRequired?.transactionType?.name === TransactionType.purchase.name ||
          fundRequired?.transactionType === 1) &&
          fundRequired?.propertyStatus === 5)
      ) {
        loanAmount += add(existingLoanBalance, otherLoansAgainstSecurity);
      } else if (
        fundRequired?.transactionType?.name === TransactionType.refinance.name ||
        fundRequired?.transactionType === 2
      ) {
        const idxFA = findIndex(
          fundingWorksheets?.fundsAvailable || [],
          (f) => f?.id === fundAvailable.id
        );
        const difference = add(
          subtract(
            subtract(existingLoanBalance, loanAmount),
            sumOfHomeLoanFundingWorksheetOtherFundAvailableContributions(
              fundingWorksheets?.fundsAvailable[idxFA]
            )
          ),
          otherLoansAgainstSecurity
        );
        if (difference > 0) {
          loanAmount += difference;
        }
      }
      hasTopUpOrExisting =
        fundRequired?.transactionType?.name === TransactionType.top_up_or_increase.name ||
        fundRequired?.transactionType === 3 ||
        fundRequired?.transactionType?.name === TransactionType.equity_release.name ||
        fundRequired?.transactionType === 4;
    }

    if (field !== 'baseLvr') {
      baseLvr =
        loanAmount && purposeFunds ? round(multiply(divide(loanAmount, purposeFunds), 100)) : 0;
    }
    if (field === 'baseLvr' && fundAvailable?.baseLvr) {
      proposedLoanAmount = hasTopUpOrExisting
        ? multiply(
            divide(fundAvailable.baseLvr, 100),
            subtract(purposeFunds, existingLoanBalance)
          )
        : multiply(divide(fundAvailable.baseLvr, 100), purposeFunds);
    }
    if (field === 'baseLvr' && !fundAvailable?.baseLvr) {
      proposedLoanAmount = 0;
    }
    totalProposedLoanAmount = fundAvailable?.isCapitalize
      ? add(toNumber(fundAvailable?.proposedLoanAmount || 0), toNumber(fundAvailable?.lmi || 0))
      : toNumber(fundAvailable?.proposedLoanAmount || 0);
    if (loanAmount && purposeFunds) {
      totalLvr = fundAvailable?.isCapitalize
        ? multiply(
            divide(add(loanAmount, toNumber(fundAvailable?.lmi || 0)), purposeFunds),
            100
          )
        : multiply(divide(loanAmount, purposeFunds), 100);
    } else {
      totalLvr = 0;
    }
  }

  return [baseLvr, proposedLoanAmount, totalProposedLoanAmount, totalLvr];
};

export const fillHomeLoanCompareProductSplit = (
  productSplit: Object,
  selectedProduct: Object,
  splitReviewProduct: Object,
  splitCompareProduct: Object
) => {
  const chosenProduct = selectedProduct;
  const reviewProductSplit = new HomeLoanReviewProductSplit(splitReviewProduct || {});
  const compareProductSplit = new HomeLoanCompareProductSplit(splitCompareProduct || {});
  reviewProductSplit.idProduct = productSplit?.idProduct || '';
  compareProductSplit.idProduct = productSplit?.idProduct || '';
  reviewProductSplit.productName = productSplit?.productName || '';
  compareProductSplit.productName = productSplit?.productName || '';
  reviewProductSplit.totalLoanAmount = productSplit?.totalLoanAmount || 0;
  reviewProductSplit.totalLoanAmountWithLmi = productSplit?.totalLoanAmountWithLmi || 0;
  compareProductSplit.totalLoanAmount = productSplit?.totalLoanAmount || 0;
  reviewProductSplit.interestRateOfProduct = productSplit?.interestRateOfProduct || 0;
  reviewProductSplit.interestRate = productSplit?.interestRate || 0;
  compareProductSplit.interestRate = productSplit?.interestRate || 0;
  reviewProductSplit.interestRateDiscount = productSplit?.interestRateDiscount || 0;
  reviewProductSplit.revertRateOfProduct = productSplit?.revertRateOfProduct || 0;
  reviewProductSplit.revertRate = productSplit?.revertRate || 0;
  compareProductSplit.revertRate = productSplit?.revertRate || 0;
  reviewProductSplit.revertRateDiscount = productSplit?.revertRateDiscount || 0;
  reviewProductSplit.comparisonRate = productSplit?.comparisonRate || 0;
  compareProductSplit.comparisonRate = productSplit?.comparisonRate || 0;
  reviewProductSplit.introTerm = productSplit?.introTerm || 0;
  reviewProductSplit.interestOnlyTerm = productSplit?.interestOnlyTerm || 0;
  reviewProductSplit.fixedTerm = productSplit?.fixedTerm || 0;
  compareProductSplit.fixedTerm = productSplit?.fixedTerm || 0;
  reviewProductSplit.interestOnlyTerm = productSplit?.interestOnlyTerm || 0;
  compareProductSplit.interestOnlyTerm = productSplit?.interestOnlyTerm || 0;
  reviewProductSplit.loanTerm = productSplit?.loanTerm || 0;
  compareProductSplit.loanTerm = productSplit?.loanTerm || 0;
  compareProductSplit.cashback = productSplit?.cashback || 0;
  compareProductSplit.offsetBalance = productSplit?.offsetBalance || 0;
  compareProductSplit.monthlyOffsetContribution = productSplit?.monthlyOffsetContribution || 0;
  reviewProductSplit.lep = productSplit?.lep || 0;
  reviewProductSplit.lmi = productSplit?.lmi || 0;
  reviewProductSplit.isEditLmi = productSplit?.isEditLmi || false;
  reviewProductSplit.isCapitalize = productSplit?.isCapitalize || false;
  reviewProductSplit.fees.application = productSplit?.fees?.application || 0;
  reviewProductSplit.fees.valuation = productSplit?.fees?.valuation || 0;
  reviewProductSplit.fees.legal = productSplit?.fees?.legal || 0;
  reviewProductSplit.fees.settlement = productSplit?.fees?.settlement || 0;
  reviewProductSplit.fees.packageMonthly = productSplit?.fees?.packageMonthly || 0;
  reviewProductSplit.fees.packageAnnual = productSplit?.fees?.packageAnnual || 0;
  reviewProductSplit.fees.rateLock = productSplit?.fees?.rateLock || 0;
  reviewProductSplit.fees.other = productSplit?.fees?.other || 0;
  reviewProductSplit.maximumBorrowing = productSplit?.maximumBorrowing || 0;
  compareProductSplit.maximumBorrowing = productSplit?.maximumBorrowing || 0;
  reviewProductSplit.ownership = productSplit?.ownership || [];
  compareProductSplit.repayment1 = productSplit?.repayment1 || 0;
  compareProductSplit.repayment1Info = productSplit?.repayment1Info || '';
  compareProductSplit.repayment2 = productSplit?.repayment2 || 0;
  compareProductSplit.repayment2Info = productSplit?.repayment2Info || '';
  compareProductSplit.repayment3 = productSplit?.repayment3 || 0;
  compareProductSplit.repayment3Info = productSplit?.repayment3Info || '';
  compareProductSplit.principalBalances = productSplit?.principalBalances || [];
  compareProductSplit.principalOffsetBalances = productSplit?.principalOffsetBalances || [];

  if (!isEmpty(chosenProduct)) {
    reviewProductSplit.rewards.hasRewards = chosenProduct?.rewards?.length > 0;
    reviewProductSplit.rewards.description = chosenProduct?.rewards?.length
      ? chosenProduct?.rewards[0]?.description
      : '';
    reviewProductSplit.rewards.points = chosenProduct?.rewards?.length
      ? chosenProduct?.rewards[0]?.name
      : '';
    delete chosenProduct.assetFinance;
    delete chosenProduct.assetFinanceEstimate;
    delete chosenProduct.homeLoanEstimate;
    delete chosenProduct.docType;
    reviewProductSplit.selectedProduct = chosenProduct || {};
  }

  return [reviewProductSplit, compareProductSplit];
};

export const fillHomeLoanCompareProduct = (
  product: Object,
  productReview: Object,
  productCompare: Object,
  isSearch: Boolean
) => {
  const reviewProduct = new HomeLoanReviewProduct(productReview);
  const compareProduct = new HomeLoanCompareProduct(productCompare);
  const selectedProduct = isSearch ? product : {};
  const productEstimate = isSearch ? product?.homeLoanEstimate[0]?.product : product;

  const pSLen = productEstimate?.productSplits?.length || 0;
  for (let p = 0; p < pSLen; p += 1) {
    const productSplit = productEstimate?.productSplits[p];
    const rIdx = findIndex(
      reviewProduct?.productSplits || [],
      (r) => r?.id === productSplit?.id
    );
    if (rIdx > -1 && !isSearch) {
      const [reviewProductSplit, compareProductSplit] = fillHomeLoanCompareProductSplit(
        productSplit,
        selectedProduct || reviewProduct.productSplits[rIdx]?.selectedProduct,
        reviewProduct.productSplits[rIdx],
        compareProduct.productSplits[rIdx]
      );
      reviewProduct.productSplits[rIdx] = reviewProductSplit;
      compareProduct.productSplits[rIdx] = compareProductSplit;
    }
    if (rIdx === -1 && isSearch) {
      const [reviewProductSplit, compareProductSplit] = fillHomeLoanCompareProductSplit(
        productSplit,
        selectedProduct
      );
      reviewProduct.productSplits.push(reviewProductSplit);
      compareProduct.productSplits.push(compareProductSplit);
    }
  }

  if (!reviewProduct.idLender) {
    reviewProduct.idLender = product?.lender[0]?.id || '';
    compareProduct.idLender = product?.lender[0]?.id || '';
    reviewProduct.lenderTurnaround = product?.lender[0]?.turnaround || '';
  }

  compareProduct.repayments.initialMonthlyRepayment =
    productEstimate?.initialMonthlyRepayment || 0;
  compareProduct.repayments.ongoingMonthlyRepayment =
    productEstimate?.ongoingMonthlyRepayment || 0;
  compareProduct.repayments.ongoingMonthlyRepaymentBuffer =
    productEstimate?.ongoingMonthlyRepaymentBuffer || 0;
  compareProduct.repayments.upfrontFees = productEstimate?.upfrontFees || 0;
  compareProduct.repayments.monthlyFees = productEstimate?.monthlyFees || 0;
  compareProduct.repayments.annualFees = productEstimate?.annualFees || 0;
  compareProduct.costs.principal = productEstimate?.totalPrincipal || 0;
  compareProduct.costs.interest = productEstimate?.totalInterest || 0;
  compareProduct.costs.interestWithOffset = productEstimate?.totalInterestWithOffset || 0;
  compareProduct.costs.fees = productEstimate?.totalFees || 0;
  compareProduct.costs.interestOffsetSavings = productEstimate?.interestOffsetSavings || 0;
  compareProduct.costs.offsetBalance = productEstimate?.totalOffsetBalance || 0;
  compareProduct.costs.monthlyOffsetContribution =
    productEstimate?.totalMonthlyOffsetContribution || 0;
  compareProduct.costs.cashback = productEstimate?.totalCashback || 0;
  compareProduct.costs.total = productEstimate?.totalCosts || 0;
  compareProduct.costs.timeSaved = productEstimate?.timeSaved || '';
  compareProduct.costs.revisedLoanTerm = productEstimate?.revisedLoanTerm || '';
  compareProduct.shortTotalCosts = productEstimate?.shortTotalCosts || 0;
  compareProduct.comparativeSaving = productEstimate?.comparativeSaving || 0;
  compareProduct.bestSaving = productEstimate?.isBestSaving || false;

  return [reviewProduct, compareProduct];
};

export const getHomeLoanTotalLoanAmount = (reviewProducts: Array<Object>, cca2: String) => {
  let loanAmountWithLmi = 0;
  const preferredProduct = find(reviewProducts || [], (r) => r?.isPreferred);
  if (preferredProduct) {
    if (cca2 === 'NZ') {
      loanAmountWithLmi = reduce(
        preferredProduct?.productSplits || [],
        (memo, n) => memo + toNumber(n?.totalLoanAmountWithLmi || 0),
        0
      );
    } else {
      loanAmountWithLmi = toNumber(
        preferredProduct?.productSplits[0]?.totalLoanAmountWithLmi || 0
      );
    }
  }
  return loanAmountWithLmi;
};

export const isHomeLoanPreferredProductFixedInterestOnlyLineOfCreditOrInterestInAdvance = (
  products: Array<Object>
) => {
  const preferredProduct = getPreferredProduct(products);
  let isFixed = false;
  let isInterestOnly = false;
  let isLineOfCredit = false;
  let isInterestInAdvance = false;
  if (preferredProduct?.productSplits?.length) {
    isInterestOnly = some(
      preferredProduct?.productSplits || [],
      (p) => p?.selectedProduct?.residential?.repaymentType === 'IO'
    );
    isInterestInAdvance = some(
      preferredProduct.productSplits,
      (p) => p?.selectedProduct?.residential?.repaymentType === 'IOA'
    );
    isFixed = some(
      preferredProduct?.productSplits || [],
      (p) => p?.selectedProduct?.residential?.rateType === 'FIXED'
    );
    isLineOfCredit = some(
      preferredProduct?.productSplits || [],
      (p) => p?.selectedProduct?.residential?.lineOfCredit === 'YES'
    );
  }
  return { isInterestOnly, isInterestInAdvance, isFixed, isLineOfCredit };
};

export const setHomeLoanComplianceCommentsForFixedInterestOnlyLineOfCreditAndInterestInAdvance =
  (reviewProducts: Array<Object>, homeLoanCompliance: Object) => {
    const compliance = homeLoanCompliance;
    if (!isEmpty(compliance)) {
      const status =
        isHomeLoanPreferredProductFixedInterestOnlyLineOfCreditOrInterestInAdvance(
          reviewProducts
        );
      compliance.comments.interestOnlyProduct = status?.isInterestOnly
        ? compliance.comments.interestOnlyProduct
        : '';
      compliance.comments.interestInAdvance = status?.isInterestInAdvance
        ? compliance.comments.interestInAdvance
        : '';
      compliance.comments.fixedProduct = status?.isFixed
        ? compliance.comments.fixedProduct
        : '';
      compliance.comments.lineOfCredit = status?.isLineOfCredit
        ? compliance.comments.lineOfCredit
        : '';
    }
    return compliance;
  };

export const fillMonthlyFinancialPosition = (
  compliance: Object,
  objective: Object,
  monthlyFinancialPosition: Object
) => {
  const cmp = compliance;
  if (!isEmpty(monthlyFinancialPosition)) {
    cmp.monthlyFinancialPosition.netIncome.currentPosition =
      monthlyFinancialPosition?.netIncome?.currentPosition || 0;
    cmp.monthlyFinancialPosition.netIncome.proposedPosition =
      monthlyFinancialPosition?.netIncome?.proposedPosition || 0;
    cmp.monthlyFinancialPosition.netIncome.proposedPositionAtBufferRate =
      monthlyFinancialPosition?.netIncome?.proposedPositionAtBufferRate || 0;
    cmp.monthlyFinancialPosition.lessTotalCurrentRepayment.currentPosition =
      monthlyFinancialPosition?.lessTotalCurrentRepayment?.currentPosition || 0;
    cmp.monthlyFinancialPosition.lessTotalCurrentRepayment.proposedPosition =
      monthlyFinancialPosition?.lessTotalCurrentRepayment?.proposedPosition || 0;
    cmp.monthlyFinancialPosition.lessTotalCurrentRepayment.proposedPositionAtBufferRate =
      monthlyFinancialPosition?.lessTotalCurrentRepayment?.proposedPositionAtBufferRate || 0;
    cmp.monthlyFinancialPosition.lessTotalProposedRepayment.currentPosition =
      monthlyFinancialPosition?.lessTotalProposedRepayment?.currentPosition || 0;
    cmp.monthlyFinancialPosition.lessTotalProposedRepayment.proposedPosition =
      monthlyFinancialPosition?.lessTotalProposedRepayment?.proposedPosition || 0;
    cmp.monthlyFinancialPosition.lessTotalProposedRepayment.proposedPositionAtBufferRate =
      monthlyFinancialPosition?.lessTotalProposedRepayment?.proposedPositionAtBufferRate || 0;
    cmp.monthlyFinancialPosition.lessTotalCurrentExpenses.currentPosition =
      monthlyFinancialPosition?.lessTotalCurrentExpenses?.currentPosition || 0;
    cmp.monthlyFinancialPosition.lessTotalCurrentExpenses.proposedPosition =
      monthlyFinancialPosition?.lessTotalCurrentExpenses?.proposedPosition || 0;
    cmp.monthlyFinancialPosition.lessTotalCurrentExpenses.proposedPositionAtBufferRate =
      monthlyFinancialPosition?.lessTotalCurrentExpenses?.proposedPositionAtBufferRate || 0;

    cmp.monthlyFinancialPosition.foreseeableFinancialChanges.total.currentPosition =
      monthlyFinancialPosition?.totalForeseeableFutureChanges?.currentPosition || 0;
    cmp.monthlyFinancialPosition.foreseeableFinancialChanges.total.proposedPosition =
      monthlyFinancialPosition?.totalForeseeableFutureChanges?.proposedPosition || 0;
    cmp.monthlyFinancialPosition.foreseeableFinancialChanges.total.proposedPositionAtBufferRate =
      monthlyFinancialPosition?.totalForeseeableFutureChanges?.proposedPositionAtBufferRate ||
      0;

    cmp.monthlyFinancialPosition.surplusDeficit.currentPosition =
      monthlyFinancialPosition?.surplusDeficit?.currentPosition || 0;
    cmp.monthlyFinancialPosition.surplusDeficit.proposedPosition =
      monthlyFinancialPosition?.surplusDeficit?.proposedPosition || 0;
    cmp.monthlyFinancialPosition.surplusDeficit.proposedPositionAtBufferRate =
      monthlyFinancialPosition?.surplusDeficit?.proposedPositionAtBufferRate || 0;

    cmp.monthlyFinancialPosition.totalAssets = monthlyFinancialPosition?.totalAssets || 0;
    cmp.monthlyFinancialPosition.totalLiabilities =
      monthlyFinancialPosition?.totalLiabilities || 0;
    cmp.monthlyFinancialPosition.netAssetPosition =
      monthlyFinancialPosition?.netAssetPosition || 0;
    cmp.monthlyFinancialPosition.totalOutgoings = monthlyFinancialPosition?.totalOutgoings || 0;
    cmp.monthlyFinancialPosition.netCashPosition =
      monthlyFinancialPosition?.netCashPosition || 0;
    cmp.monthlyFinancialPosition.financialRisk =
      objective?.getObjectiveFinancialSecurityTotal();
    cmp.monthlyFinancialPosition.hemVariance = monthlyFinancialPosition?.hemVariance || 0;
    cmp.monthlyFinancialPosition.debtToIncomeRatio =
      monthlyFinancialPosition?.debtToIncomeRatio || 0;
  }

  return cmp;
};

export default {
  getIdLabelsOfTicketClients,
  isClientClient,
  isClientNonBorrowingSpouse,
  isClientSecurityGuarantor,
  isClientServicingGuarantor,
  isThereAnyContactWhoIsCompanyOrSelfEmployed,
  areAllContactsCompanies,
  isHouseholdComposedOnlyOfSecurityGuarantorClients,
  isSelfEmployedInACompany,
  hasEducationDebtPerContact,
  getAvailableLendersByLenderCommissions,
  getPayment,
  getPurchaseOwnerOccupiedStatusOfHomeLoanSecurity,
  getBusinessYear,
  isLiabilityLinkedToAssetMarkedAsToBeSold,
  getInvestmentAssets,
  getInvestmentHomeLoanSecurities,
  getOwnerOccupiedAssets,
  setOwnershipOfAssetOrLiability,
  getOwnershipPercentageOfAssetOrLiabilityPerContact,
  getPurchaseOwnerOccupiedStatusOfAssets,
  getPostSettlementStatus,
  getPurchaseOwnerOccupiedStatus,
  getOrganizationCountry,
  getStateAndPostCodePerContact,
  getPreferredProduct,
  fillAssetFinanceCompareProduct,
  fillAssetFinanceFinanceProposal,
  setAssetFinanceFinanceProposalAmortizationSchedules,
  getHomeLoanSecurityValue,
  getHomeLoanPropertyStatus,
  getHomeLoanOwnershipType,
  getHomeLoanTransactionType,
  sumOfHomeLoanFundingWorksheetOtherFundAvailableContributions,
  updateHomeLoanFundingWorksheetTotalProposedLoanAmountAndLvr,
  fillHomeLoanCompareProductSplit,
  fillHomeLoanCompareProduct,
  getHomeLoanTotalLoanAmount,
  isHomeLoanPreferredProductFixedInterestOnlyLineOfCreditOrInterestInAdvance,
  setHomeLoanComplianceCommentsForFixedInterestOnlyLineOfCreditAndInterestInAdvance,
  fillMonthlyFinancialPosition
};
